import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import Reveal from "../components/Reveal";
import { companies } from "../data/companies";

export default function Portfolio() {
  return (
    <div data-testid="portfolio-page" className="bg-[#070707]">
      {/* HEADER */}
      <section className="px-6 pt-40 pb-20 md:px-12 md:pt-52 md:pb-28">
        <div className="mx-auto max-w-[1600px]">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#E83D16]"
          >
            [ Our Media Companies ]
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
            className="mt-6 font-display text-[15vw] leading-[0.86] tracking-tighter text-[#F5F5F0] md:text-[11vw]"
          >
            Portfolio
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mt-8 max-w-xl font-body text-base leading-relaxed text-[#A1A1AA] md:text-lg"
          >
            A growing network of media brands built for reach, trust and
            influence across the professional world.
          </motion.p>
        </div>
      </section>

      {/* COMPANIES */}
      <section className="px-6 pb-32 md:px-12">
        <div className="mx-auto max-w-[1600px] space-y-px">
          {companies.map((c, idx) => (
            <Reveal key={c.id} delay={idx * 0.1}>
              <article
                data-testid={`company-card-${c.id}`}
                className="group grid grid-cols-1 gap-10 border-t border-[#27272A] py-14 md:grid-cols-12"
              >
                {/* index + image */}
                <div className="md:col-span-5">
                  <span className="font-mono-accent text-sm text-[#A1A1AA]">
                    0{idx + 1} &nbsp;/&nbsp; 0{companies.length}
                  </span>
                  <Link
                    to={`/portfolio/${c.id}`}
                    data-testid={`company-image-link-${c.id}`}
                    className="relative mt-5 block overflow-hidden"
                  >
                    <img
                      src={c.image}
                      alt={c.name}
                      className="h-80 w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0 md:h-[26rem]"
                    />
                    <div className="absolute inset-0 bg-black/30" />
                    <span
                      className="absolute left-5 top-5 flex h-14 w-14 items-center justify-center bg-[#E83D16] font-display text-2xl text-white"
                      aria-hidden="true"
                    >
                      {c.name.charAt(0)}
                    </span>
                  </Link>
                </div>

                {/* details */}
                <div className="md:col-span-6 md:col-start-7">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <Link
                      to={`/portfolio/${c.id}`}
                      data-testid={`company-title-link-${c.id}`}
                    >
                      <h2 className="font-display text-5xl tracking-tight text-[#F5F5F0] transition-colors duration-300 hover:text-[#E83D16] md:text-6xl">
                        {c.name}
                      </h2>
                    </Link>
                    <span className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#E83D16]">
                      {c.platform}
                    </span>
                  </div>
                  <p className="mt-2 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">
                    {c.category} &mdash; Founded {c.founded}
                  </p>

                  <p className="mt-6 font-body text-base leading-relaxed text-[#A1A1AA]">
                    {c.description}
                  </p>

                  <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {c.highlights.map((h) => (
                      <li
                        key={h}
                        className="flex items-start gap-3 font-body text-sm text-[#F5F5F0]"
                      >
                        <Check
                          size={16}
                          className="mt-0.5 shrink-0 text-[#E83D16]"
                        />
                        {h}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10 flex items-end justify-between border-t border-[#27272A] pt-6">
                    <div className="flex gap-10">
                      <div>
                        <p className="font-display text-4xl text-[#F5F5F0]">
                          {c.followers}
                        </p>
                        <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                          Followers
                        </p>
                      </div>
                      <div>
                        <p className="font-display text-4xl text-[#F5F5F0]">
                          {c.founded}
                        </p>
                        <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                          Founded
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Link
                        to={`/portfolio/${c.id}`}
                        data-testid={`company-view-${c.id}`}
                        className="btn-editorial group/view inline-flex items-center gap-2 border border-[#F5F5F0] px-6 py-3 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#F5F5F0] hover:text-white"
                      >
                        View Company
                        <ArrowUpRight
                          size={14}
                          className="transition-transform duration-300 group-hover/view:translate-x-1 group-hover/view:-translate-y-1"
                        />
                      </Link>
                      <a
                        href={c.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`company-link-${c.id}`}
                        aria-label={`Visit ${c.name} on ${c.platform}`}
                        className="link-sweep font-mono-accent text-xs uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#E83D16]"
                      >
                        Visit
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}

          {/* more coming */}
          <Reveal>
            <div className="border-t border-[#27272A] py-20 text-center">
              <p className="font-display text-3xl italic text-[#A1A1AA] md:text-4xl">
                More media brands joining the group soon.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
